import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";
import Record from "slotworks/model/gameplay/records/record";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import AnticipationSupplement from "slotworks/model/gameplay/supplements/anticipation-supplement";
import { DataProcessorSupplement } from "slotworks/model/gameplay/supplements/data-processor-supplement";
import FreespinSymbolSubstituteSupplement from "slotworks/model/gameplay/supplements/freespin-symbol-substitute-supplement";
import GridOnlySupplement from "slotworks/model/gameplay/supplements/grid-only-supplement";
import betModel from "slotworks/model/bets/bet-model";

export class DataProcessorSupplementCollection {
    private dataProcessorSupplements: DataProcessorSupplement[] = [FreespinSymbolSubstituteSupplement, AnticipationSupplement, GridOnlySupplement];

    public addAfterStandard(dataProcessorSupplement: DataProcessorSupplement) {
        this.dataProcessorSupplements.push(dataProcessorSupplement);
    }

    public addBeforeStandard(dataProcessorSupplement: DataProcessorSupplement) {
        this.dataProcessorSupplements.unshift(dataProcessorSupplement);
    }

    public get() {
        return this.dataProcessorSupplements;
    }
}

export default class Gameplay {

    public static dataProcessorSupplements: DataProcessorSupplementCollection = new DataProcessorSupplementCollection();

    /**
     * Unique identifier for this gameplay
     */
    public id: string;

    /**
     * Balance at the current moment of this gameplay
     */
    public balance: number;

    /**
     * Total bet the player has made at this moment on this gameplay
     */
    public totalBet: number;

    /**
     * Total credits won at this moment
     */
    public totalCreditsWon: number = 0;

    /**
     * Total credits won at this moment byy BASE game
     */
    public totalBaseCreditsWon: number = 0;

    /**
     * Total credits won at this moment by freespins games
     */
    public totalFreespinsCreditsWon: number = 0;

    /**
     * Balance when the gameplay began, after stake was deducted
     */
    public startBalance: number;

    /**
     * Array of records associated with this gameplay
     */
    public records: Record[] = [];

    /**
     * Is it base game or bonus game
     */
    public inFreespinsGame: boolean = false;

    /**
     * Whether or not a gameplay has been recovered (due to a user disconnect etc)
     */
    public isRecovered: boolean;

    /** Is the game buyout bonus */
    public isBuyout: boolean = false;

    /**
     * When previous free spin records are lost during recovery, it can be necesary to just have a total win value
     */
    public recoveredFreeSpinTotalWin: number = 0;

    /**
     * When previous free spins are recovered, the total won may need to be recovered
     */
    public recoveredTotalFreespins: number = 0;

    /**
     * When the entire game has been displayed and is considered complete
     */
    public complete: boolean = false;

    /**
     * The record currently being displayed
     */
    private currentRecordIndex: number = 0;

    private triggeringRecordIndex: number = 0;

    public addRecord(record: Record) {
        for (const dataProcessorSupplements of Gameplay.dataProcessorSupplements.get()) {
            dataProcessorSupplements.process(record);
        }

        this.records.push(record);
    }

    public getCurrentRecordIndex(): number {
        return this.currentRecordIndex;
    }

    public getFirstRecord(): Record {
        return this.records[0];
    }

    public getCurrentRecord(): Record {
        const index = this.getCurrentRecordIndex();
        return this.records.length > index ? this.records[index] : null;
    }

    public getPreviousRecord(): Record {
        const index = this.getCurrentRecordIndex();

        return index > 0 ? this.records[index - 1] : null;
    }

    public latestRecord(): Record {
        this.setRecordIndex(this.records.length - 1);

        return this.getCurrentRecord();
    }

    public nextRecord(): Record {
        if (this.currentRecordIndex < this.records.length - 1) {
            this.setRecordIndex(this.currentRecordIndex + 1);
        } else {
            return null;
        }

        return this.getCurrentRecord();
    }

    public previousRecord(): Record {
        if (this.currentRecordIndex > 0) {
            this.setRecordIndex(this.currentRecordIndex - 1);
        } else {
            return null;
        }

        return this.getCurrentRecord();
    }

    public getNextRecord(): Record {
        const index = this.getCurrentRecordIndex();

        return this.records.length > index + 1 ? this.records[index + 1] : null;
    }

    public getRecordAtIndex(index: number): Record {
        return (index >= 0 && index < this.records.length) ? this.records[index] : null;
    }

    public resetRecordIndex(): Record {
        return this.resetRecordToIndex(0);
    }

    public resetRecordToIndex(index: number): Record {
        if (index < 0 || index >= this.records.length) {
            throw new Error("Invalid attempt to set record index. Trying to se to record " + index + " of " + this.records.length);
        }

        this.setRecordIndex(index);

        return this.getCurrentRecord();
    }

    public hasMoreRecords(): boolean {
        return this.getCurrentRecordIndex() < this.records.length - 1;
    }

    public getRunningWinTotal() {
        let runningTotal = 0;

        for (let i = this.getCurrentRecordIndex(); i >= 0; i--) {
            const record = this.getRecordAtIndex(i);
            runningTotal += record.creditsWon;
        }

        return runningTotal;
    }

    public getRunningFreespinsWinTotal() {
        let runningTotal = this.recoveredFreeSpinTotalWin;

        if (this.records && this.records.length) {
            const firstFreespinRecordIndex = this.getFirstFreespinRecordIndex();

            if (firstFreespinRecordIndex !== -1) {
                for (let i = firstFreespinRecordIndex; i <= this.getCurrentRecordIndex(); i++) {
                    const record = this.getRecordAtIndex(i);
                    runningTotal += record.creditsWon;
                }
            }
        }

        return runningTotal;
    }

    public getTotalFreespinsWon() {
        let totalFreespins = this.recoveredTotalFreespins;

        for (let i = this.getCurrentRecordIndex(); i >= 0; i--) {
            const record = this.getRecordAtIndex(i) as SpinRecord;
            if (record.freespinsWon > 0) {
                totalFreespins += record.freespinsWon;
            }
        }

        return totalFreespins;
    }

    public getTotalWinLoss() {
        return this.totalCreditsWon - this.totalBet;
    }

    public isWinAboveBet() {
        const win = this.inFreespinsGame ? this.totalFreespinsCreditsWon : this.totalBaseCreditsWon;
        if(this.totalBet)
            return win - this.totalBet > 0;
        else 
            return win - betModel.getTotalBet() > 0;
    }

    // Will reset to last active spin record or free spin record
    public resetToTriggeringSpin(): Record {
        this.resetRecordToIndex(this.triggeringRecordIndex);

        return this.getCurrentRecord();
    }

    private setRecordIndex(index: number) {
        this.currentRecordIndex = index;

        const currentRecord = this.getCurrentRecord();
        if (currentRecord instanceof SpinRecord || currentRecord instanceof FreespinRecord) {
            this.triggeringRecordIndex = index;
        }
    }

    private getFirstFreespinRecordIndex() {
        let firstFreespinRecordIndex = this.getCurrentRecordIndex();
        for (let i = this.getCurrentRecordIndex(); i >= 0; i--) {
            const record = this.getRecordAtIndex(i);
            if (record instanceof FreespinRecord) {
                firstFreespinRecordIndex = i;
            }
        }

        if (firstFreespinRecordIndex === this.getCurrentRecordIndex() && !(this.getCurrentRecord() instanceof FreespinRecord)) {
            return -1;
        }

        return firstFreespinRecordIndex;
    }
}



// WEBPACK FOOTER //
// ./~/slotworks/model/gameplay/gameplay-model.ts